import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    Box
} from '@mui/material';
import { UserDataFieldDto, BusinessDataFieldType } from '../../types/customUserDataFields';
import { customUserDataFieldService, SignatureConstraints } from '../../services/customUserDataFieldService';
import SignaturePreview from './SignaturePreview';

interface CustomFieldDialogProps {
    field: UserDataFieldDto | null;
    open: boolean;
    onClose: () => void;
    onSave: (fieldId: string, value: string) => Promise<void>;
    /**
     * Handler used to upload a signature image. Required for Signature-type fields;
     * the parent supplies the correct (self vs admin) upload call.
     */
    onUploadSignature?: (fieldId: string, file: File) => Promise<void>;
}

const CustomFieldDialog: React.FC<CustomFieldDialogProps> = ({
    field,
    open,
    onClose,
    onSave,
    onUploadSignature
}) => {
    const [value, setValue] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
    const [constraints, setConstraints] = useState<SignatureConstraints | null>(null);

    useEffect(() => {
        customUserDataFieldService.getSignatureConstraints()
            .then(setConstraints)
            .catch(err => console.error('Failed to load signature constraints:', err));
    }, []);

    useEffect(() => {
        if (field) {
            setValue(field.value || '');
            setError('');
            setSelectedFile(null);
        }
    }, [field]);

    // Generate (and revoke) an object URL to preview the newly selected file.
    useEffect(() => {
        if (!selectedFile) {
            setLocalPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(selectedFile);
        setLocalPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [selectedFile]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Reset the input so re-selecting the same file still fires onChange.
        e.target.value = '';
        if (!file) return;

        if (constraints && !constraints.acceptedContentTypes.includes(file.type)) {
            setError('Signature must be a PNG or JPEG image.');
            return;
        }
        if (constraints && file.size > constraints.maxSizeBytes) {
            const maxMb = Math.round(constraints.maxSizeBytes / 1024 / 1024);
            setError(`Signature image must not exceed ${maxMb} MB.`);
            return;
        }

        setError('');
        setSelectedFile(file);
    };

    const handleSave = async () => {
        if (!field) return;

        const isSignature = field.businessDataField.type === BusinessDataFieldType.Signature;

        try {
            setIsSaving(true);
            setError('');

            if (isSignature) {
                if (!selectedFile) {
                    // Nothing new chosen — nothing to save.
                    onClose();
                    return;
                }
                if (!onUploadSignature) {
                    setError('Signature upload is not available.');
                    return;
                }
                await onUploadSignature(field.businessDataFieldId, selectedFile);
            } else {
                await onSave(field.businessDataFieldId, value);
            }
        } catch (err) {
            console.error('Error saving field:', err);
            setError('Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            setValue('');
            setError('');
            setSelectedFile(null);
            onClose();
        }
    };

    const renderFieldInput = () => {
        if (!field) return null;

        const { type, name, description } = field.businessDataField;

        switch (type) {
            case BusinessDataFieldType.Bool:
                return (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={value === 'true'}
                                onChange={(e) => setValue(e.target.checked ? 'true' : 'false')}
                                color="primary"
                            />
                        }
                        label={`${name} ${value === 'true' ? '(Yes)' : '(No)'}`}
                    />
                );

            case BusinessDataFieldType.Text:
                return (
                    <TextField
                        fullWidth
                        label={name}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        helperText={description}
                        inputProps={{ maxLength: 2000 }}
                        multiline={false}
                    />
                );

            case BusinessDataFieldType.MultilineText:
                return (
                    <TextField
                        fullWidth
                        label={name}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        helperText={description}
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 2000 }}
                    />
                );

            case BusinessDataFieldType.Signature:
                return (
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            {selectedFile && localPreviewUrl ? (
                                <Box
                                    component="img"
                                    src={localPreviewUrl}
                                    alt="Selected signature"
                                    sx={{
                                        maxHeight: 120,
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 0.5,
                                    }}
                                />
                            ) : value ? (
                                <SignaturePreview value={value} thumbnailBase64={field?.signatureThumbnailBase64} maxHeight={120} />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No signature uploaded yet.
                                </Typography>
                            )}
                        </Box>
                        <Button variant="outlined" component="label" disabled={isSaving}>
                            {value || selectedFile ? 'Replace image' : 'Choose image'}
                            <input
                                type="file"
                                hidden
                                accept="image/png,image/jpeg"
                                onChange={handleFileChange}
                            />
                        </Button>
                        <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            PNG or JPEG, up to 2 MB.
                        </Typography>
                    </Box>
                );

            default:
                return (
                    <Typography color="error">
                        Unsupported field type: {type}
                    </Typography>
                );
        }
    };

    if (!field) return null;

    const isSignature = field.businessDataField.type === BusinessDataFieldType.Signature;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit {field.businessDataField.name}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {field.businessDataField.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {field.businessDataField.description}
                        </Typography>
                    )}

                    {renderFieldInput()}

                    {error && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isSaving || (isSignature && !selectedFile)}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomFieldDialog;
